import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CrownOutlined,
	MobileOutlined,
} from "@ant-design/icons";
import {
	useUserProfile,
	useUserSubscription,
	useVerifySubscription,
} from "@bodhistreams/api/src";
import { BodhiLoader, VideoBackground } from "@bodhistreams/ui";
import { Button, Result, Space, Tag, Typography, theme } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageLayout from "./PageLayout";

const { Title, Text } = Typography;
const { useToken } = theme;

const SubscriptionSuccess = () => {
	const navigate = useNavigate();
	const { token } = useToken();
	const [searchParams] = useSearchParams();
	const sessionId = searchParams.get("session_id");

	const [verifying, setVerifying] = useState(true);
	const pollRef = useRef(null);
	const attemptRef = useRef(0);

	const { data: userData, refetch: refetchUser } = useUserProfile();
	const { data: subscriptionData, refetch: refetchSubscription } =
		useUserSubscription(false);
	const { mutateAsync: verifySubscription } = useVerifySubscription();

	useEffect(() => {
		if (!sessionId) return;

		const verify = async () => {
			attemptRef.current += 1;
			try {
				const result = await verifySubscription(sessionId);
				if (result?.verified) {
					await refetchUser();
					await refetchSubscription();
					setVerifying(false);
					if (pollRef.current) clearInterval(pollRef.current);
					return;
				}
			} catch (_e) {
				// Continue polling on error
			}
			// Stop after 10 attempts (~30s)
			if (attemptRef.current >= 10) {
				await refetchUser();
				await refetchSubscription();
				setVerifying(false);
				if (pollRef.current) clearInterval(pollRef.current);
			}
		};

		// First attempt immediately
		verify();
		// Then poll every 3 seconds
		pollRef.current = setInterval(verify, 3000);

		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, [refetchSubscription, refetchUser, sessionId, verifySubscription]);

	if (!sessionId) {
		return (
			<PageLayout
				fullWidthContent={
					<VideoBackground
						showBlueGradient={false}
						showTopWhiteGradient={false}
						showBottomWhiteGradient={false}
						showVerticalGradient={true}
						darkOverlayOpacity="rgba(0, 0, 0, 0.1)"
						containerStyle={{
							padding: "40px 24px",
							textAlign: "center",
							minHeight: "100vh",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
						}}
						contentStyle={{
							maxWidth: "600px",
						}}
					>
						<Result
							status="error"
							title="Invalid Session"
							subTitle="No session ID found. Please try again."
							extra={[
								<Button type="primary" key="home" onClick={() => navigate("/")}>
									Back to Home
								</Button>,
							]}
							style={{
								background: token.colorBgContainer,
								border: `1px solid ${token.colorBorder}`,
								borderRadius: token.borderRadiusLG,
								padding: "32px",
							}}
						/>
					</VideoBackground>
				}
			/>
		);
	}

	if (verifying) {
		return (
			<PageLayout
				fullWidthContent={
					<VideoBackground
						showBlueGradient={false}
						showTopWhiteGradient={false}
						showBottomWhiteGradient={false}
						showVerticalGradient={true}
						darkOverlayOpacity="rgba(0, 0, 0, 0.1)"
						containerStyle={{
							padding: "40px 24px",
							textAlign: "center",
							minHeight: "100vh",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
						}}
						contentStyle={{
							maxWidth: "600px",
						}}
					>
						<div
							style={{
								background: token.colorBgContainer,
								border: `1px solid ${token.colorBorder}`,
								borderRadius: token.borderRadiusLG,
								padding: "48px 32px",
								textAlign: "center",
							}}
						>
							<BodhiLoader mode="inline" size={64} backgroundOpacity={0} />
							<div
								style={{
									marginTop: 24,
									fontSize: 24,
									fontWeight: "bold",
									color: token.colorPrimary,
								}}
							>
								Verifying your subscription...
							</div>
							<div
								style={{
									marginTop: 12,
									color: token.colorTextSecondary,
									fontSize: 16,
								}}
							>
								This may take a few moments
							</div>
						</div>
					</VideoBackground>
				}
			/>
		);
	}

	// Build membership object from user data
	const membership = userData
		? {
				name: userData.membership_name,
				price: userData.membership_price,
				max_simultaneous_devices: userData.membership_max_simultaneous_devices,
			}
		: null;

	const subscriptionDetails = subscriptionData
		? {
				selected_price: `$${subscriptionData.price || membership?.price || 0}`,
				price_period:
					subscriptionData.billing_period === "yearly" ? "year" : "month",
				billing_cycle: subscriptionData.billing_period || "monthly",
				is_yearly: subscriptionData.billing_period === "yearly",
			}
		: null;

	// Helper to parse price string like "$19.99"
	const parsePrice = (priceStr) => {
		if (!priceStr) return 0;
		return parseFloat(priceStr.replace(/[^0-9.]/g, ""));
	};

	return (
		<PageLayout
			fullWidthContent={
				<VideoBackground
					showBlueGradient={false}
					showTopWhiteGradient={false}
					showBottomWhiteGradient={false}
					showVerticalGradient={true}
					darkOverlayOpacity="rgba(0, 0, 0, 0.1)"
					containerStyle={{
						padding: "40px 24px",
						textAlign: "center",
						minHeight: "100vh",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
					contentStyle={{
						maxWidth: "600px",
					}}
				>
					<div
						style={{
							background: token.colorBgContainer,
							borderRadius: token.borderRadiusLG,
							padding: "48px 40px",
							textAlign: "center",
							boxShadow: token.boxShadow,
							width: "100%",
							maxWidth: "500px",
							border: `1px solid ${token.colorBorder}`,
						}}
					>
						{/* Success Icon */}
						<div style={{ marginBottom: "24px" }}>
							<CheckCircleOutlined
								style={{
									color: token.colorSuccess,
									fontSize: "80px",
								}}
							/>
						</div>

						{/* Main Title */}
						<Title
							level={1}
							style={{
								color: token.colorText,
								marginBottom: "16px",
								fontSize: "32px",
								fontWeight: "700",
							}}
						>
							Thanks for subscribing!
						</Title>

						{/* Membership Info */}
						{membership && (
							<div style={{ marginBottom: "32px" }}>
								{/* Plan Header */}
								<div
									style={{
										background: token.colorPrimaryBg,
										borderRadius: token.borderRadius,
										padding: "24px",
										marginBottom: "24px",
										border: `1px solid ${token.colorPrimaryBorder}`,
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "12px",
											marginBottom: "16px",
										}}
									>
										<CrownOutlined
											style={{ fontSize: "28px", color: token.colorPrimary }}
										/>
										<Text
											strong
											style={{ fontSize: "24px", color: token.colorText }}
										>
											{membership.name} Plan
										</Text>
									</div>

									{/* Pricing */}
									{subscriptionDetails && (
										<div style={{ textAlign: "center" }}>
											<div style={{ marginBottom: "12px" }}>
												<Text
													style={{
														fontSize: "36px",
														fontWeight: "bold",
														color: token.colorText,
													}}
												>
													$
													{parsePrice(
														subscriptionDetails.selected_price,
													).toFixed(2)}
												</Text>
												<Text
													style={{
														fontSize: "18px",
														color: token.colorTextSecondary,
														marginLeft: "4px",
													}}
												>
													/{subscriptionDetails.price_period}
												</Text>
											</div>

											<div
												style={{
													display: "flex",
													justifyContent: "center",
													gap: "8px",
													flexWrap: "wrap",
												}}
											>
												<Tag
													color={
														subscriptionDetails.is_yearly
															? "gold"
															: token.colorPrimary
													}
													style={{ fontSize: "12px", fontWeight: "bold" }}
												>
													{subscriptionDetails.billing_cycle.toUpperCase()}
												</Tag>
												{subscriptionDetails.is_yearly &&
													membership.yearly_price && (
														<Tag
															color="gold"
															style={{ fontSize: "12px", fontWeight: "bold" }}
														>
															Save{" "}
															{Math.round(
																(1 -
																	parseFloat(membership.yearly_price) /
																		(parseFloat(membership.price) * 12)) *
																	100,
															)}
															%
														</Tag>
													)}
											</div>
										</div>
									)}
								</div>

								{/* Features */}
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: "16px",
										marginBottom: "24px",
									}}
								>
									<div
										style={{
											padding: "20px",
											background: token.colorBgLayout,
											border: `1px solid ${token.colorBorderSecondary}`,
											borderRadius: token.borderRadius,
											textAlign: "center",
										}}
									>
										<MobileOutlined
											style={{
												fontSize: "32px",
												color: token.colorInfo,
												marginBottom: "8px",
												display: "block",
											}}
										/>
										<Text
											style={{
												fontSize: "12px",
												color: token.colorTextSecondary,
												textTransform: "uppercase",
												letterSpacing: "1px",
												display: "block",
												marginBottom: "4px",
											}}
										>
											Devices
										</Text>
										<Text
											strong
											style={{ fontSize: "24px", color: token.colorText }}
										>
											{membership.max_simultaneous_devices}
										</Text>
									</div>

									<div
										style={{
											padding: "20px",
											background: token.colorBgLayout,
											border: `1px solid ${token.colorBorderSecondary}`,
											borderRadius: token.borderRadius,
											textAlign: "center",
										}}
									>
										<ClockCircleOutlined
											style={{
												fontSize: "32px",
												color: token.colorInfo,
												marginBottom: "8px",
												display: "block",
											}}
										/>
										<Text
											style={{
												fontSize: "12px",
												color: token.colorTextSecondary,
												textTransform: "uppercase",
												letterSpacing: "1px",
												display: "block",
												marginBottom: "4px",
											}}
										>
											Streaming
										</Text>
										<Text
											strong
											style={{ fontSize: "24px", color: token.colorText }}
										>
											{membership.streaming_hours
												? `${membership.streaming_hours}h`
												: "Unlimited"}
										</Text>
									</div>
								</div>
							</div>
						)}

						{/* Success Message */}
						<div style={{ marginBottom: "32px" }}>
							<Text
								style={{
									fontSize: "16px",
									color: token.colorTextSecondary,
									lineHeight: "1.6",
									display: "block",
								}}
							>
								🎉 Your subscription is now active! Enjoy unlimited access to
								premium features and content.
							</Text>
						</div>

						{/* Action Buttons */}
						<Space
							size="large"
							orientation="vertical"
							style={{ width: "100%" }}
						>
							<Button
								type="primary"
								size="large"
								onClick={() => navigate("/")}
								style={{
									width: "100%",
									height: "48px",
									borderRadius: token.borderRadius,
									fontSize: "16px",
									fontWeight: "600",
								}}
							>
								Start Creating Content
							</Button>
							<Button
								size="large"
								onClick={() => navigate("/profile")}
								style={{
									width: "100%",
									height: "48px",
									borderRadius: token.borderRadius,
									fontSize: "16px",
								}}
							>
								View Profile
							</Button>
						</Space>
					</div>
				</VideoBackground>
			}
		/>
	);
};

export default SubscriptionSuccess;
